/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
/* eslint-disable @typescript-eslint/no-unused-expressions */

import React, { useState, useEffect, useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import {
  Grid,
  Divider,
  CardMedia,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Tabs,
  Tab,
  createMuiTheme,
  ThemeProvider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

// UUID

// React Inline SVG
import SVG from 'react-inlinesvg';

// Leaflet
import L from 'leaflet';
import * as ReactLeaflet from 'react-leaflet';
//@ts-ignore
import 'leaflet.glify';

// Swipeable Views
import SwipeableViews from 'react-swipeable-views';

// Components
import CloseIcon from 'components/Icons/Close';
import { ModalTemplate } from 'components/ModalTemplate';
import { LineSkeleton } from 'components/LineSkeleton';

// Leaflet Markers
import * as MarkerIcons from 'components/Leaflet/MarkerIcons';

// Constants
import CONST_IMAGES from 'constants/images';
import CONST_TRIPS from 'constants/trips';

// Helpers
import { getTripOccurrencesQuantityByType } from 'helpers/trips';
import { wait } from 'helpers/wait';
import { Capitalize } from 'helpers/capitalize';
import { formatDateTimezone } from 'helpers/formatDateTimezone';

// Hooks
import useMount from 'hooks/useMount';

// Actions
import {
  getTripsRequest,
  tripAskMissingEntriesRequest,
} from 'store/ducks/trips/actions';
import { setSecondClose, setOpen } from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';

// Components
import { TabPanel } from 'components/TabPanel';
import DialogImage from 'components/DialogImage/DialogImage';
import { ICurrentTrip } from '../../pages/bdos/TripMonitoring/components/TripMonitoringRoot';
import Chart from '../../pages/bdos/TripMonitoring/components/Chart';
import MapTab from '../../pages/bdos/TripMonitoring/components/MapTab';
import PAYMENTS from 'constants/payments';

// Styles
import useStyles from './tripReportDialog.styles';

interface TripModalInterface {
  tripId: string;
  filtersList?: any;
}

type Props = TripModalInterface;

const TripReportDialog: React.FC<Props> = ({ tripId, filtersList }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const MAP_TAB = 1;

  const [errorStartImage, setErrorStartImage] = useState(false);
  const [errorEndImage, setErrorEndImage] = useState(false);
  const [currentTrips, setCurrentTrips] = useState<ICurrentTrip[]>([]); // rendered trips

  const [currNavGuide, setCurrNavGuide] = useState(0);
  const [responseData, setResponseData] = useState<any>();

  const tripData = useSelector(
    (state: ApplicationState) => state.trips.tripData,
  );
  const mount = useMount();

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPause, setIsPause] = useState(true);
  const [tableReference, setTableReference] = useState('');
  const [
    tripMapInstance,
    setTripMapInstance,
  ] = useState<ReactLeaflet.Map | null>(null);

  useEffect(() => {
    try {
      if (
        tripMapInstance?.leafletElement &&
        currNavGuide === MAP_TAB &&
        tripData?.TripRoute &&
        tripData?.TripRoute?.length > 0
      ) {
        const tripRoute = tripData?.TripRoute || [];
        const map = tripMapInstance?.leafletElement;
        //@ts-ignore
        L.glify.points({
          size: 8,
          color: '#000',
          map,
          data: tripRoute.map(({ lat, lng }) => [lat, lng]),
          click: (_e: any, point: any) => {
            const lat = Array.isArray(point) ? point[0] : point.lat;
            const lng = Array.isArray(point) ? point[1] : point.lng;
            const closest = tripRoute.reduce((prev, curr) => {
              const dPrev =
                Math.abs(prev.lat - lat) + Math.abs(prev.lng - lng);
              const dCurr =
                Math.abs(curr.lat - lat) + Math.abs(curr.lng - lng);
              return dCurr < dPrev ? curr : prev;
            });
            L.popup()
              .setLatLng([lat, lng])
              .setContent(
                new Date(closest.timestamp).toLocaleString('pt-BR'),
              )
              .openOn(map);
            return true;
          },
        });
      }
    } catch (er) {
      console.log(er);
    }
  }, [tripData.TripRoute, tripMapInstance?.leafletElement]);

  useEffect(() => {
    wait(500).then(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tripId) {
      const getTrip = async () => {
        try {
          const response = (await dispatch(
            getTripsRequest({
              params: {
                id: tripId,
                _includes: 'occurrence,metric,linestop,car,control',
              },
              recovers: 'lineroute,triproute',
            }),
          )) as any;

          if (response?.status === 200) {
            if (response?.data?.id) {
              setResponseData(response?.data);
            }
          } else {
            setError(true);
          }
        } catch (error) {
          setError(true);
        }
      };

      getTrip();
    }
  }, [dispatch, tripId]);

  const isEqual = useMemo(() => {
    if (tripData && tripData?.EntriesResume) {
      return tripData.totalPassengers === tripData.EntriesResume.quantity;
    } else {
      return false;
    }
  }, [tripData]);

  const diferenceCount = useMemo(() => {
    if (
      (tripData?.totalPassengers || tripData?.totalPassengers === 0) &&
      (tripData?.totalExitPassengers || tripData?.totalExitPassengers === 0)
    ) {
      return tripData.totalPassengers - tripData.totalExitPassengers;
    }
    return 0;
  }, [tripData]);

  const handleChangeNavigation = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    setCurrNavGuide(newValue);
  };

  const muiTheme = createMuiTheme({
    palette: {
      secondary: {
        main: '#0094FF',
      },
    },
  });

  const getMetricValue = useMemo(
    () => (type: number) => {
      const value =
        tripData?.TripMetrics?.find((mt) => mt.type === type)?.value || 0;
      if (
        type === CONST_TRIPS.TRIP.METRICS.REGULARITY.key ||
        type === CONST_TRIPS.TRIP.METRICS.PUNCTUALITY.key
      ) {
        return Math.round(value * 100);
      }
      return value;
    },
    [tripData.TripMetrics],
  );

  const formatSelectedLines = useMemo(
    () => ({
      Sections: [
        {
          LineSection: tripData.LineSection,
        },
      ],
    }),
    [tripData.LineSection],
  );

  if (!mount) return null;
  return (
    <ModalTemplate
      hideCloseIcon
      classes={{
        root: classes.overwritePd,
        container: classes.overwritePd,
      }}>
      <Grid container>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Grid
            container
            justifyContent='flex-end'
            alignItems='center'
            className={classes.icons}>
            <Grid item>
              <IconButton
                onClick={() => {
                  if (!isPause) setIsPause(true);
                  wait(50).then(() => dispatch(setSecondClose({})));
                }}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Box width='100%' mb={4} ml={3}>
          <Typography display='inline' className={classes.title}>
            Laudo da Viagem
          </Typography>
        </Box>

        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          {/* Navigation Guide */}
          <ThemeProvider theme={muiTheme}>
            <Tabs
              value={currNavGuide}
              textColor='secondary'
              indicatorColor='secondary'
              className={classes.tabs}
              onChange={handleChangeNavigation}>
              <Tab label='Informações Básicas' />
              <Tab label='Mapa de viagem' />
              <Tab label='Tabela de viagem' />
              <Tab label='Ocorrências' />
              <Tab label='Passagens' />
              <Tab label='Registro de Km' />
              <Tab label='Auditoria de catraca' />
            </Tabs>
          </ThemeProvider>

          <SwipeableViews
            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
            index={currNavGuide}>
            <TabPanel
              index={0}
              value={currNavGuide}
              boxStyles={{ overflow: 'hidden' }}>
              <div style={{ overflow: 'hidden' }}>
                <Grid
                  container
                  justifyContent='space-between'
                  alignItems='center'
                  className={classes.infoContainer}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box display='flex' flexDirection='column' ml={2}>
                      <Typography style={{ fontSize: 24 }}>
                        Viagem{' '}
                        {tripData?.id
                          ? `${tripData?.id?.split('-')?.[0]?.toUpperCase() ||
                          ''}`
                          : ''}{' '}
                      </Typography>

                      <Box display='flex' mt={4}>
                        <Box display='flex' alignItems='center'>
                          {' '}
                          <span
                            style={{
                              backgroundColor:
                                tripData?.LineSection?.Line?.color,
                            }}
                            className={classes.linePrefix}>
                            {' '}
                            {tripData?.LineSection?.Line.prefix
                              ? tripData?.LineSection?.Line.prefix
                              : ''}{' '}
                          </span>
                          <span className={classes.infoText}>
                            {' '}
                            {tripData?.LineSection?.Line.name
                              ? tripData?.LineSection?.Line.name
                              : ''}{' '}
                          </span>
                        </Box>
                      </Box>

                      <Box display='flex' mt={1}>
                        <Typography
                          display='inline'
                          className={classes.infoText}>
                          Prefixo do Veículo:
                        </Typography>
                        <Typography
                          display='inline'
                          style={{ marginLeft: 8 }}
                          className={classes.infoText}>
                          {tripData.Car?.prefix || '--'}
                        </Typography>
                      </Box>

                      <Box display='flex' mt={1}>
                        <Typography
                          display='inline'
                          className={classes.infoText}>
                          Motorista:{' '}
                        </Typography>
                        <Typography
                          display='inline'
                          style={{ marginLeft: 8 }}
                          className={classes.infoText}>
                          {tripData?.TransportEmployee?.name || '--'}
                        </Typography>
                      </Box>
                      <Box display='flex' mt={1}>
                        <Typography
                          display='inline'
                          className={classes.infoText}>
                          DBD:{' '}
                        </Typography>
                        <Typography
                          display='inline'
                          style={{ marginLeft: 8 }}
                          className={classes.infoText}>
                          {tripData.dbdId}
                        </Typography>
                      </Box>

                      <Box display='flex' mt={4}>
                        <Typography
                          display='inline'
                          className={classes.infoText}>
                          Início:
                        </Typography>
                        <Typography
                          display='inline'
                          style={{ marginLeft: 8 }}
                          className={classes.infoText}>
                          {formatDateTimezone(
                            tripData.startedAt,
                            'dd/MM/yyyy • HH:mm:ss',
                          )}
                        </Typography>
                      </Box>

                      <Box display='flex' mt={1}>
                        <Typography
                          display='inline'
                          className={classes.infoText}>
                          Fim:
                        </Typography>
                        <Typography
                          display='inline'
                          style={{ marginLeft: 8 }}
                          className={classes.infoText}>
                          {formatDateTimezone(
                            tripData.closedAt,
                            'dd/MM/yyyy • HH:mm:ss',
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box width='80%' ml={4}>
                      <Box
                        width='100%'
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'>
                        <Box width='50%' className={classes.card}>
                          <Grid
                            container
                            justifyContent='space-between'
                            alignItems='center'>
                            <Grid item>
                              <Box display='flex' flexDirection='column'>
                                <Typography
                                  display='inline'
                                  style={{ fontSize: 20 }}>
                                  Regularidade
                                </Typography>
                                <Typography
                                  display='inline'
                                  style={{ fontSize: 20, fontWeight: 500 }}>
                                  {`${getMetricValue(
                                    CONST_TRIPS.TRIP.METRICS.REGULARITY.key,
                                  )}%`}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item>
                              <Box marginLeft={1.5}>
                                <Chart
                                  percent={getMetricValue(
                                    CONST_TRIPS.TRIP.METRICS.REGULARITY.key,
                                  )}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>

                        <Box width='50%' className={classes.card} ml={2}>
                          <Grid
                            container
                            justifyContent='space-between'
                            alignItems='center'>
                            <Grid item>
                              <Box display='flex' flexDirection='column'>
                                <Typography
                                  display='inline'
                                  style={{ fontSize: 20 }}>
                                  Pontualidade
                                </Typography>
                                <Typography
                                  display='inline'
                                  style={{ fontSize: 20, fontWeight: 500 }}>
                                  {`${getMetricValue(
                                    CONST_TRIPS.TRIP.METRICS.PUNCTUALITY.key,
                                  )}%`}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item>
                              <Box marginLeft={1.5}>
                                <Chart
                                  percent={getMetricValue(
                                    CONST_TRIPS.TRIP.METRICS.PUNCTUALITY.key,
                                  )}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>

                      <Box className={classes.card} mt={2}>
                        <Grid
                          container
                          justifyContent='space-between'
                          alignItems='center'>
                          <Grid item>
                            <Box display='flex' flexDirection='column'>
                              <Typography
                                display='inline'
                                style={{ fontSize: 20 }}>
                                Velocidade Média
                              </Typography>
                              <Typography
                                display='inline'
                                style={{ fontSize: 20, fontWeight: 500 }}>
                                {`${getMetricValue(
                                  CONST_TRIPS.TRIP.METRICS.AVG_SPEED.key,
                                )} km/h`}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item>
                            <Box display='flex' marginLeft={1.5}>
                              <SVG
                                src={CONST_IMAGES.ICONS.WATCH}
                                title='Velocidade Média'
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </div>
            </TabPanel>

            <TabPanel
              index={1}
              value={currNavGuide}
              boxStyles={{ overflow: 'hidden' }}>
              <div style={{ overflow: 'hidden' }}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Box className={classes.mapBox}>
                    <MapTab
                      setTripMapInstance={setTripMapInstance}
                      isPause={isPause}
                      setIsPause={setIsPause}
                      filterList={filtersList}
                      selectedLines={[formatSelectedLines] || []}
                      currentTrips={currentTrips}
                      setCurrentTrips={setCurrentTrips}
                    >
                      {tripData.LineSection?.Line?.LineStops?.filter(
                        (ls) => ls.lineSectionId === tripData.lineSectionId,
                      ).map((lineStop) => {
                        const stop = lineStop.Stop;
                        if (!stop?.startLat || !stop?.startLong) return null;
                        const stopOccurrences =
                          tripData.TripOccurrences?.filter(
                            (occ) => occ.lineStopId === lineStop.id,
                          ) || [];
                        const isOk = stopOccurrences.some(
                          (occ) =>
                            occ.type ===
                            CONST_TRIPS.TRIP.OCCURRENCES.STOP_ON_TIME.key,
                        );
                        return (
                          <ReactLeaflet.Marker
                            key={`stop-${lineStop.id}`}
                            position={[stop.startLat, stop.startLong]}
                            icon={
                              isOk
                                ? MarkerIcons.blueIcon
                                : MarkerIcons.redIcon
                            }
                          >
                            <ReactLeaflet.Popup>
                              <div>
                                <strong>{stop.name}</strong>
                                {stopOccurrences.length > 0 ? (
                                  <ul style={{ margin: '8px 0 0', paddingLeft: 16 }}>
                                    {stopOccurrences.map((occ) => {
                                      const label =
                                        Object.values(
                                          CONST_TRIPS.TRIP.OCCURRENCES,
                                        ).find((o) => o.key === occ.type)
                                          ?.label || '';
                                      const datetime = occ.occurrenceDatetime
                                        ? new Date(
                                            occ.occurrenceDatetime,
                                          ).toLocaleString('pt-BR')
                                        : '';
                                      return (
                                        <li key={occ.id}>
                                          {label}
                                          {datetime ? ` - ${datetime}` : ''}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <p style={{ margin: '8px 0 0' }}>
                                    Sem ocorrências
                                  </p>
                                )}
                              </div>
                            </ReactLeaflet.Popup>
                          </ReactLeaflet.Marker>
                        );
                      })}
                    </MapTab>
                  </Box>
                </Grid>
              </div>
            </TabPanel>

            <TabPanel
              index={2}
              value={currNavGuide}
              boxStyles={{ overflow: 'hidden' }}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className={classes.tableContainer}>
                  <TableContainer component={Paper} className={classes.table}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell variant='head' align='left'>
                            Parada
                          </TableCell>
                          <TableCell variant='head' align='left'>
                            Partida Prog.
                          </TableCell>
                          <TableCell variant='head' align='left'>
                            Partida
                          </TableCell>
                          <TableCell variant='head' align='left'>
                            Status
                          </TableCell>
                          <TableCell variant='head' align='left'>
                            Atrasos
                          </TableCell>
                          <TableCell variant='head' align='left'>
                            Adiantamentos
                          </TableCell>
                          <TableCell variant='head' align='left'>
                            Sem Parada
                          </TableCell>
                          <TableCell variant='head' align='left'>
                            Desvios
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      {!loading && (
                        <TableBody>
                          {Array.isArray(
                            tripData.LineSection?.Line.LineStops,
                          ) &&
                            [...(tripData.LineSection?.Line.LineStops || [])]
                              .filter(
                                (ls) =>
                                  ls.lineSectionId === tripData.lineSectionId,
                              )
                              .sort((a, b) => a.order - b.order)
                              .map((lineStop) => (
                                <TableRow key={lineStop.id}>
                                  <TableCell>
                                    {lineStop?.Stop?.name || '--'}
                                  </TableCell>
                                  {(() => {
                                    const occ =
                                      tripData.TripOccurrences?.find(
                                        (o) => o.lineStopId === lineStop.id,
                                      );
                                    return (
                                      <>
                                        <TableCell>
                                          {occ?.expectedDatetime
                                            ? formatDateTimezone(
                                                occ.expectedDatetime,
                                                'dd/MM/yyyy • HH:mm:ss',
                                              )
                                            : '--'}
                                        </TableCell>
                                        <TableCell>
                                          {occ?.occurrenceDatetime
                                            ? formatDateTimezone(
                                                occ.occurrenceDatetime,
                                                'dd/MM/yyyy • HH:mm:ss',
                                              )
                                            : '--'}
                                        </TableCell>
                                        <TableCell>
                                          {occ?.type ===
                                          CONST_TRIPS.TRIP.OCCURRENCES
                                            .STOP_ON_TIME.key
                                            ? 'OK'
                                            : 'Não OK'}
                                        </TableCell>
                                      </>
                                    );
                                  })()}
                                  <TableCell>
                                    {getTripOccurrencesQuantityByType(
                                      tripData,
                                      CONST_TRIPS.TRIP.OCCURRENCES.STOP_DELAYED
                                        .key,
                                      lineStop.id,
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {getTripOccurrencesQuantityByType(
                                      tripData,
                                      CONST_TRIPS.TRIP.OCCURRENCES.STOP_ADVANCED
                                        .key,
                                      lineStop.id,
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {getTripOccurrencesQuantityByType(
                                      tripData,
                                      CONST_TRIPS.TRIP.OCCURRENCES.STOP_NO_STOP
                                        .key,
                                      lineStop.id,
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {getTripOccurrencesQuantityByType(
                                      tripData,
                                      CONST_TRIPS.TRIP.OCCURRENCES.ROUTE_AWAY
                                        .key,
                                      lineStop.id,
                                    )}
                                  </TableCell>
                                </TableRow>
                              ),
                            )}
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                  {loading && <LineSkeleton generateCount={5} height={80} />}
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel
              index={3}
              value={currNavGuide}
              boxStyles={{ overflow: 'hidden' }}>
              <Grid container className={classes.occurrenciesContainer}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Divider variant='fullWidth' />
                  <Accordion
                    elevation={0}
                    defaultExpanded
                    className={classes.accordion}
                    TransitionProps={{ timeout: 300 }}>
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon style={{ color: '#14171A' }} />
                      }
                      aria-controls='integration-info-content'>
                      <Typography style={{ color: '#14171A', fontSize: 20 }}>
                        Atrasos
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box width='100%' display='flex' flexDirection='column'>
                        {Array.isArray(responseData?.TripOccurrences) &&
                          responseData?.TripOccurrences?.length > 0
                          ? responseData?.TripOccurrences?.map(
                            (ocurrency: any) => {
                              if (
                                ocurrency?.type ===
                                CONST_TRIPS.TRIP.OCCURRENCES.STOP_DELAYED.key
                              ) {
                                return (
                                  <Box
                                    display='flex'
                                    justifyContent='flex-start'
                                    alignItems='center'
                                    className={classes.card}
                                    mb={2}>
                                    <Box display='flex'>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.occurrenceDatetime
                                          ? `${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleDateString(
                                            'pt-BR',
                                          )} • ${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleTimeString('pt-BR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}`
                                          : '--'}
                                      </Typography>
                                      <Typography
                                        style={{
                                          fontSize: 20,
                                          marginRight: 8,
                                          marginLeft: 8,
                                        }}>
                                        {' '}
                                        {' - '}{' '}
                                      </Typography>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.LineStop?.Stop?.Address
                                          ?.street
                                          ? `${ocurrency?.LineStop?.Stop
                                            ?.Address?.street
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.street
                                            : ''
                                          }, ${ocurrency?.LineStop?.Stop
                                            ?.Address?.number
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.number
                                            : ''
                                          }`
                                          : ''}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              }
                            },
                          )
                          : null}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Divider variant='fullWidth' />
                  <Accordion
                    elevation={0}
                    defaultExpanded
                    className={classes.accordion}
                    TransitionProps={{ timeout: 300 }}>
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon style={{ color: '#14171A' }} />
                      }
                      aria-controls='integration-info-content'>
                      <Typography style={{ color: '#14171A', fontSize: 20 }}>
                        Adiantamentos
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box width='100%' display='flex' flexDirection='column'>
                        {Array.isArray(responseData?.TripOccurrences) &&
                          responseData?.TripOccurrences?.length > 0
                          ? responseData?.TripOccurrences?.map(
                            (ocurrency: any) => {
                              if (
                                ocurrency?.type ===
                                CONST_TRIPS.TRIP.OCCURRENCES.STOP_ADVANCED.key
                              ) {
                                return (
                                  <Box
                                    display='flex'
                                    justifyContent='flex-start'
                                    alignItems='center'
                                    className={classes.card}
                                    mb={2}>
                                    <Box display='flex'>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.occurrenceDatetime
                                          ? `${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleDateString(
                                            'pt-BR',
                                          )} • ${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleTimeString('pt-BR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}`
                                          : '--'}
                                      </Typography>
                                      <Typography
                                        style={{
                                          fontSize: 20,
                                          marginRight: 8,
                                          marginLeft: 8,
                                        }}>
                                        {' '}
                                        {' - '}{' '}
                                      </Typography>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.LineStop?.Stop?.Address
                                          ?.street
                                          ? `${ocurrency?.LineStop?.Stop
                                            ?.Address?.street
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.street
                                            : ''
                                          }, ${ocurrency?.LineStop?.Stop
                                            ?.Address?.number
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.number
                                            : ''
                                          }`
                                          : ''}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              }
                            },
                          )
                          : null}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Divider variant='fullWidth' />
                  <Accordion
                    elevation={0}
                    defaultExpanded
                    className={classes.accordion}
                    TransitionProps={{ timeout: 300 }}>
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon style={{ color: '#14171A' }} />
                      }
                      aria-controls='integration-info-content'>
                      <Typography style={{ color: '#14171A', fontSize: 20 }}>
                        Sem Parada
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box width='100%' display='flex' flexDirection='column'>
                        {Array.isArray(responseData?.TripOccurrences) &&
                          responseData?.TripOccurrences?.length > 0
                          ? responseData?.TripOccurrences?.map(
                            (ocurrency: any) => {
                              if (
                                ocurrency?.type ===
                                CONST_TRIPS.TRIP.OCCURRENCES.STOP_NO_STOP.key
                              ) {
                                return (
                                  <Box
                                    display='flex'
                                    justifyContent='flex-start'
                                    alignItems='center'
                                    className={classes.card}
                                    mb={2}>
                                    <Box display='flex'>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.occurrenceDatetime
                                          ? `${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleDateString(
                                            'pt-BR',
                                          )} • ${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleTimeString('pt-BR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}`
                                          : '--'}
                                      </Typography>
                                      <Typography
                                        style={{
                                          fontSize: 20,
                                          marginRight: 8,
                                          marginLeft: 8,
                                        }}>
                                        {' '}
                                        {' - '}{' '}
                                      </Typography>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.LineStop?.Stop?.Address
                                          ?.street
                                          ? `${ocurrency?.LineStop?.Stop
                                            ?.Address?.street
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.street
                                            : ''
                                          }, ${ocurrency?.LineStop?.Stop
                                            ?.Address?.number
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.number
                                            : ''
                                          }`
                                          : ''}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              }
                            },
                          )
                          : null}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Divider variant='fullWidth' />
                  <Accordion
                    elevation={0}
                    defaultExpanded
                    className={classes.accordion}
                    TransitionProps={{ timeout: 300 }}>
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon style={{ color: '#14171A' }} />
                      }
                      aria-controls='integration-info-content'>
                      <Typography style={{ color: '#14171A', fontSize: 20 }}>
                        Desvios
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box width='100%' display='flex' flexDirection='column'>
                        {Array.isArray(responseData?.TripOccurrences) &&
                          responseData?.TripOccurrences?.length > 0
                          ? responseData?.TripOccurrences?.map(
                            (ocurrency: any) => {
                              if (
                                ocurrency?.type ===
                                CONST_TRIPS.TRIP.OCCURRENCES.ROUTE_AWAY.key
                              ) {
                                return (
                                  <Box
                                    display='flex'
                                    justifyContent='flex-start'
                                    alignItems='center'
                                    className={classes.card}
                                    mb={2}>
                                    <Box display='flex'>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.occurrenceDatetime
                                          ? `${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleDateString(
                                            'pt-BR',
                                          )} • ${new Date(
                                            ocurrency?.occurrenceDatetime,
                                          )?.toLocaleTimeString('pt-BR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}`
                                          : '--'}
                                      </Typography>
                                      <Typography
                                        style={{
                                          fontSize: 20,
                                          marginRight: 8,
                                          marginLeft: 8,
                                        }}>
                                        {' '}
                                        {' - '}{' '}
                                      </Typography>
                                      <Typography
                                        className={classes.cardText}>
                                        {ocurrency?.LineStop?.Stop?.Address
                                          ?.street
                                          ? `${ocurrency?.LineStop?.Stop
                                            ?.Address?.street
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.street
                                            : ''
                                          }, ${ocurrency?.LineStop?.Stop
                                            ?.Address?.number
                                            ? ocurrency?.LineStop?.Stop
                                              ?.Address?.number
                                            : ''
                                          }`
                                          : ''}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              }
                            },
                          )
                          : null}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel
              index={4}
              value={currNavGuide}
              boxStyles={{ overflow: 'hidden' }}>
              <Grid container className={classes.occurrenciesContainer}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Typography
                    style={{ color: '#14171A', fontSize: 20, paddingLeft: 16 }}>
                    Passagens Embarcadas: {tripData?.totalPassengers}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Divider variant='fullWidth' />
                  <Accordion
                    elevation={0}
                    defaultExpanded
                    className={classes.accordion}
                    TransitionProps={{ timeout: 300 }}>
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon style={{ color: '#14171A' }} />
                      }
                      aria-controls='integration-info-content'>
                      <Typography
                        style={{
                          color: '#14171A',
                          fontSize: 20,
                        }}>
                        Passagens Enviadas: {tripData?.EntriesResume?.quantity}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box width='100%' display='flex' flexDirection='column'>
                        <TableContainer
                          component={Paper}
                          className={classes.table}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell variant='head' align='left'>
                                  Forma de Embarque
                                </TableCell>
                                <TableCell variant='head' align='left'>
                                  Quantidade
                                </TableCell>
                                <TableCell variant='head' align='left'>
                                  Valor
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tripData?.EntriesResume?.entries.payments &&
                                Object.entries(
                                  tripData?.EntriesResume?.entries.payments,
                                ).map(([key, data]) => (
                                  <TableRow key={key}>
                                    <TableCell align='left'>
                                      {Capitalize(
                                        PAYMENTS.ENTRY.find(
                                          ({ KEY }) => KEY === key,
                                        )?.VALUE || '',
                                      )}
                                    </TableCell>
                                    <TableCell align='left'>
                                      {data.quantity}
                                    </TableCell>
                                    <TableCell align='left'>
                                      R$ {data.value.toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {!isEqual && (
                          <Box pt={2}>
                            <Button
                              className={classes.askMissingEntries}
                              onClick={() =>
                                dispatch(
                                  tripAskMissingEntriesRequest({
                                    id: tripData.id,
                                  }),
                                )
                              }>
                              Buscar Passagens
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Divider variant='fullWidth' />
                  <Accordion
                    elevation={0}
                    defaultExpanded
                    className={classes.accordion}
                    TransitionProps={{ timeout: 300 }}>
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon style={{ color: '#14171A' }} />
                      }
                      aria-controls='integration-info-content'>
                      <Typography style={{ color: '#14171A', fontSize: 20 }}>
                        Beneficios aplicados
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box width='100%' display='flex' flexDirection='column'>
                        <TableContainer
                          component={Paper}
                          className={classes.table}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell variant='head' align='left'>
                                  Perfil
                                </TableCell>
                                <TableCell variant='head' align='left'>
                                  Quantidade
                                </TableCell>
                                <TableCell variant='head' align='left'>
                                  Valor
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tripData?.EntriesResume?.entries.benefits &&
                                Object.entries(
                                  tripData?.EntriesResume?.entries.benefits,
                                ).map(([key, data]) => (
                                  <TableRow key={key}>
                                    <TableCell align='left'>
                                      {Capitalize(key)}
                                    </TableCell>
                                    <TableCell align='left'>
                                      {data.quantity}
                                    </TableCell>
                                    <TableCell align='left'>
                                      R$ {data.value.toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel
              index={5}
              value={currNavGuide}
              boxStyles={{ overflow: 'hidden' }}>
              <Box display='flex' width='100%' alignContent='center' pt={4}>
                <Box
                  display='flex'
                  width='40%'
                  m={1}
                  mr={2}
                  className={classes.contentImages}>
                  <Box display='flex' width='100%' pr={1}>
                    <img
                      alt='Imagem'
                      style={{ width: 400, height: 400, marginRight: 8 }}
                      className={classes.imageContainerDisabled}
                      src={
                        tripData?.TripControl?.startKmPhoto
                          ? tripData?.TripControl?.startKmPhoto
                          : ''
                      }
                      onError={(e: any) => {
                        if (
                          e.target.src !==
                          CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE
                        ) {
                          e.target.onerror = null;
                          e.target.src =
                            CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE;
                        }
                      }}
                    />
                  </Box>
                  <Box display='flex' width='100%'>
                    <img
                      alt='Imagem'
                      style={{ width: 400, height: 400, marginRight: 8 }}
                      className={classes.imageContainerDisabled}
                      src={
                        tripData?.TripControl?.endKmPhoto
                          ? tripData?.TripControl?.endKmPhoto
                          : ''
                      }
                      onError={(e: any) => {
                        if (
                          e.target.src !==
                          CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE
                        ) {
                          e.target.onerror = null;
                          e.target.src =
                            CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE;
                        }
                      }}
                    />
                  </Box>
                </Box>
                <Box display='flex' width='60%'>
                  <TableContainer className={classes.table}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Km Inicial</TableCell>
                          <TableCell>Km Final</TableCell>
                          <TableCell>Km Rodados</TableCell>
                          <TableCell>Km Ocioso</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            {tripData?.TripControl
                              ? tripData?.TripControl?.startKm
                              : 0}{' '}
                            km
                          </TableCell>
                          <TableCell>
                            {tripData?.TripControl
                              ? tripData?.TripControl?.endKm
                              : 0}{' '}
                            km
                          </TableCell>
                          <TableCell>
                            {tripData?.km ? tripData?.km : 0} km
                          </TableCell>
                          <TableCell>
                            {tripData?.idleKm ? tripData?.idleKm : 0} km
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </TabPanel>
            <TabPanel
              index={6}
              value={currNavGuide}
              boxStyles={{ overflow: 'hidden' }}>
              <Box display='flex' width='100%' alignContent='center' pt={4}>
                <Box
                  display='flex'
                  width='40%'
                  m={1}
                  mr={2}
                  className={classes.contentImages}>
                  {!errorStartImage ? (
                    <CardMedia
                      alt='Imagem'
                      component='img'
                      style={{ width: 400, height: 400, marginRight: 8 }}
                      className={classes.imageContainer}
                      src={
                        responseData?.TripControl?.startEntryCountPhoto
                          ? responseData?.TripControl?.startEntryCountPhoto
                          : ''
                      }
                      onError={(e: any) => {
                        setErrorStartImage(true);

                        if (
                          e.target.src !==
                          CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE
                        ) {
                          e.target.onerror = null;
                          e.target.src =
                            CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE;
                        }
                      }}
                      onClick={() => {
                        dispatch(
                          setOpen({
                            open: true,
                            content: (
                              <DialogImage
                                image={
                                  responseData?.TripControl
                                    ?.startEntryCountPhoto
                                    ? responseData?.TripControl
                                      ?.startEntryCountPhoto
                                    : ''
                                }
                              />
                            ),
                          }),
                        );
                      }}
                    />
                  ) : (
                    <img
                      alt='Imagem'
                      style={{ width: 400, height: 400, marginRight: 8 }}
                      className={classes.imageContainerDisabled}
                      src={
                        responseData?.TripControl?.startEntryCountPhoto
                          ? responseData?.TripControl?.startEntryCountPhoto
                          : ''
                      }
                      onError={(e: any) => {
                        if (
                          e.target.src !==
                          CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE
                        ) {
                          e.target.onerror = null;
                          e.target.src =
                            CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE;
                        }
                      }}
                    />
                  )}

                  {!errorEndImage ? (
                    <CardMedia
                      alt='Imagem'
                      component='img'
                      style={{ width: 400, height: 400, marginRight: 8 }}
                      className={classes.imageContainer}
                      src={
                        responseData?.TripControl?.endEntryCountPhoto
                          ? responseData?.TripControl?.endEntryCountPhoto
                          : ''
                      }
                      onError={(e: any) => {
                        setErrorEndImage(true);

                        if (
                          e.target.src !==
                          CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE
                        ) {
                          e.target.onerror = null;
                          e.target.src =
                            CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE;
                        }
                      }}
                      onClick={() => {
                        dispatch(
                          setOpen({
                            open: true,
                            content: (
                              <DialogImage
                                image={
                                  responseData?.TripControl?.endEntryCountPhoto
                                    ? responseData?.TripControl
                                      ?.endEntryCountPhoto
                                    : ''
                                }
                              />
                            ),
                          }),
                        );
                      }}
                    />
                  ) : (
                    <img
                      alt='Imagem'
                      style={{ width: 400, height: 400, marginRight: 8 }}
                      className={classes.imageContainerDisabled}
                      src={
                        responseData?.TripControl?.endEntryCountPhoto
                          ? responseData?.TripControl?.endEntryCountPhoto
                          : ''
                      }
                      onError={(e: any) => {
                        if (
                          e.target.src !==
                          CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE
                        ) {
                          e.target.onerror = null;
                          e.target.src =
                            CONST_IMAGES.BACKGROUND_IMAGES.BLUE_BUBBLE;
                        }
                      }}
                    />
                  )}
                </Box>
                <Box display='flex' width='60%'>
                  <Box width='100%'>
                    <TableContainer
                      className={classes.table}
                      ref={(ref: any) => setTableReference(ref)}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell variant='head' align='left'>
                              Cat. Entrada Inicial
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Cat. Entrada Final
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Total Giros
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Registro DBD
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Diferença
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tripData && (
                            <TableRow>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.startEntryCount
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.endEntryCount
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.resultEntryCount
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData?.totalPassengers
                                  ? tripData.totalPassengers
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {' '}
                                <Box
                                  display='flex'
                                  alignItems='center'
                                  minWidth='37px'>
                                  {diferenceCount}
                                  {tripData.TripControl &&
                                    tripData &&
                                    diferenceCount < 0 && (
                                      <img
                                        src={CONST_IMAGES.ICONS.DOWN_ARROW}
                                        alt='negative value'
                                      />
                                    )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TableContainer
                      className={classes.table}
                      ref={(ref: any) => setTableReference(ref)}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell variant='head' align='left'>
                              Cat. Saída Inicial
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Cat. Saída Final
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Total Giros
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Registro DBD
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Diferença
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tripData && (
                            <TableRow>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.startExitCount
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.endExitCount
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.resultExitCount
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData?.totalExitPassengers
                                  ? tripData.totalExitPassengers
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {' '}
                                <Box
                                  display='flex'
                                  alignItems='center'
                                  minWidth='37px'>
                                  {diferenceCount}
                                  {tripData.TripControl &&
                                    tripData &&
                                    diferenceCount < 0 && (
                                      <img
                                        src={CONST_IMAGES.ICONS.DOWN_ARROW}
                                        alt='negative value'
                                      />
                                    )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TableContainer
                      className={classes.table}
                      ref={(ref: any) => setTableReference(ref)}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell variant='head' align='left'>
                              Q.B. Entrada Inicial
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Q.B. Entrada Final
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Total de Quedas
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Registro DBD
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Diferença
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tripData && (
                            <TableRow>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.startArmDrop
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.endArmDrop
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.resultArmDrop
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl?.rollsArmDrop ?? 0}
                              </TableCell>
                              <TableCell>
                                <Box
                                  display='flex'
                                  alignItems='center'
                                  minWidth='37px'>
                                  {(tripData.TripControl?.rollsArmDrop || 0) -
                                    (tripData.TripControl?.resultArmDrop || 0)}
                                  {tripData.TripControl &&
                                    (tripData.TripControl?.rollsArmDrop || 0) -
                                      (tripData.TripControl?.resultArmDrop ||
                                        0) <
                                      0 && (
                                      <img
                                        src={CONST_IMAGES.ICONS.DOWN_ARROW}
                                        alt='negative value'
                                      />
                                    )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TableContainer
                      className={classes.table}
                      ref={(ref: any) => setTableReference(ref)}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell variant='head' align='left'>
                              Q.B. Saída Inicial
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Q.B. Saída Final
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Total de Quedas
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Registro DBD
                            </TableCell>
                            <TableCell variant='head' align='left'>
                              Diferença
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tripData && (
                            <TableRow>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.startArmExitDrop
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.endArmExitDrop
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl
                                  ? tripData.TripControl?.resultArmExitDrop
                                  : 0}
                              </TableCell>
                              <TableCell>
                                {tripData.TripControl?.rollsArmExitDrop ?? 0}
                              </TableCell>
                              <TableCell>
                                <Box
                                  display='flex'
                                  alignItems='center'
                                  minWidth='37px'>
                                  {(tripData.TripControl?.rollsArmExitDrop ||
                                    0) -
                                    (tripData.TripControl?.resultArmExitDrop ||
                                      0)}
                                  {tripData.TripControl &&
                                    (tripData.TripControl?.rollsArmExitDrop ||
                                      0) -
                                      (tripData.TripControl
                                        ?.resultArmExitDrop || 0) <
                                      0 && (
                                      <img
                                        src={CONST_IMAGES.ICONS.DOWN_ARROW}
                                        alt='negative value'
                                      />
                                    )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </Box>
            </TabPanel>
          </SwipeableViews>
        </Grid>
      </Grid>
    </ModalTemplate>
  );
};

export default TripReportDialog;
export { TripReportDialog };
